import rootLogger from '@/utils/root-logger';
import fetcher from '@/utils/fetcher';
import {sessionStorage} from '@/utils/storage';
import telemetry from '@/utils/telemetry';
import windowUtils from '@/utils/window-utils';
import {formatUrl} from '@/utils/url-utils';
import {generateVttFromStt} from '@/utils/generateVttFromStt'
import { SESSION_STATUS } from './g2w-session';

const logger = rootLogger.create('store/modules/g2w-simulive');

export class Token {
  constructor(t) {
    if (t instanceof Token) {
      return t;
    } if (t) {
      const o = JSON.parse(t);

      Token.props.forEach((prop) => {
        this[prop] = o[prop];
      });
    }
  }

  set tokenish(tokenish) {
    const o = Token.parseQueryParameter(tokenish);
    this.bearer = o.access_token;
    this.expires = Date.now() + (Number(o.expires_in) * 1000);
  }

  isValid() {
    return this.expires && (Number(this.expires) > Date.now());
  }

  stringify() {
    const o = {
      bearer: this.bearer,
      expires: this.expires
    };
    return JSON.stringify(o);
  }

  static get props() { return ['bearer', 'expires'] }

  static parseQueryParameter(q) {
    const o = {};
    if (q[0] === '/') {
      q = q.substring(1);
    }
    const qs = decodeURI(q).split('&');
    qs.forEach((pair) => {
      const ps = pair.split('=');
      o[ps[0]] = ps[1];
    });
    return o;
  }
}

const mutations = {
  setToken(state, payload) {
    state.token = payload.token;
  },

  setTokenish(state, payload) {
    if (!state.token) {
      state.token = new Token(undefined);
    }
    state.token.tokenish = payload.tokenish;
    sessionStorage.setItem('token_string', state.token.stringify());
  },

  setSettings(state, payload) {
    state.settings = payload.settings;
  }
};

const actions = {
  async join({commit, rootState, rootGetters, dispatch}, payload) {

    let joinInfo;

    try {
      const simuliveServiceUrl = rootGetters['g2wEnv/simuliveServiceUrl'];
      const webinarKey = payload.webinarKey;

      let url = `${simuliveServiceUrl}/events/${webinarKey}`;

      if (rootState.previewMode) {
        const token = await dispatch('getToken', {webinarKey});
        if (!token) {
          return Promise.resolve(); // redirected to auth service url
        }
        const fetchParams = {
          headers: new Headers({
            Authorization: `Bearer ${token.bearer}`
          })
        };

        joinInfo = await fetcher.fetchJSON(url, fetchParams, {operation: 'getEventInfo'});
      } else {
        const registrantKey = await dispatch('g2wWebinar/normalizeRegistrantKey', payload, { root: true });

        url += `/attendees/${registrantKey}`;
        const fetchParams = {
          method: 'POST'
        };

        joinInfo = await fetcher.fetchJSON(url, fetchParams, {operation: 'createAttendeeRecord'});
      }

      commit('g2wSession/setJoinInfo', {joinInfo}, {root: true});
      dispatch('g2wSimulive/getSettings', {webinarKey})

      if (joinInfo.contentFile) {
        // online recording
        let contentFile = rootState.testMode ? 'static/test/testVideo.mp4' : joinInfo.contentFile;
        commit('recording/setContentFile', {contentFile: formatUrl(contentFile)}, {root: true});
      } else if (joinInfo.brightcoveVideoId) {
        // brightcove (manual) recording
        commit('recording/setBrightcoveVideoId', {brightcoveVideoId: `ref:${joinInfo.brightcoveVideoId}`}, {root: true});
      } else {
        throw new Error('missing recording details', joinInfo);
      }

      if(joinInfo.parsedTranscriptFile) {
        const transcript = await fetcher.fetchJSON(joinInfo.parsedTranscriptFile)
        if(transcript.utterances) {
          const transcriptFile = generateVttFromStt(transcript.utterances)
          commit('recording/setTranscriptFile', {transcriptFile}, {root: true})
        }
      }

      let eventFile = rootState.testMode ? 'static/test/events.vtt' : joinInfo.eventFile;
      if (eventFile) {
        commit('recording/setEventFile', {eventFile: formatUrl(eventFile)}, {root: true});
      }

      const timeOffsetMS = joinInfo.timeOffsetMS || 0;
      const startPosition = Math.max(timeOffsetMS, 0) / 1000;
      commit('recording/setStartPosition', {startPosition}, {root: true});

      logger.log('webinar join time', {timeOffsetMS, startPosition});

      if (timeOffsetMS < 0) {
        telemetry.track(rootState.g2wEnv.current, 'joined scheduled webinar early', {secondsEarly: -timeOffsetMS / 1000});
        commit('g2wSession/setSessionStatus', { sessionStatus: SESSION_STATUS.waitStart }, { root: true });

        setTimeout(() => {
          dispatch('g2wSession/questions/fetchSettings', {}, {root: true});
          commit('g2wSession/setSessionStatus', { sessionStatus: SESSION_STATUS.inSession }, { root: true });
        }, Math.abs(timeOffsetMS));
      } else {
        dispatch('g2wSession/questions/fetchSettings', {}, {root: true});
        commit('g2wSession/setSessionStatus', { sessionStatus: SESSION_STATUS.inSession }, { root: true });
      }
    } catch (error) {
      logger.error('simlive join error', error);
      throw error;
    }

    return joinInfo;
  },

  async getToken({commit, state, rootState}, payload) {
    logger.log('getToken');

    // From Session
    const tokenString = sessionStorage.getItem('token_string');

    const token = new Token(tokenString);
    commit('setToken', {token});

    if (state.token.isValid()) {
      return token;
    }

    const {webinarKey} = payload;
    sessionStorage.setItem('webinarKey', webinarKey);

    const location = windowUtils.getParsedUrl(true);
    location.set('hash', null);
    const redirectURI = encodeURIComponent(location);

    const authServiceUrl = `${rootState.g2wEnv.current.authServiceBaseUrl}/oauth/authorize?` +
      `response_type=token&client_id=${rootState.g2wEnv.current.authServiceClientId}&` +
      `redirect_uri=${redirectURI}`;

    windowUtils.locationReplace(authServiceUrl);
    return null;
  },

  setTokenish({commit}, payload) {
    commit('setTokenish', payload);
  },

  async leave({rootState, rootGetters}, payload = {}) {
    const left = (success) => {
      logger.log('left', {success});
      return success;
    };

    return Promise.resolve()
      .then(() => {
        if (rootState.previewMode) {
          return left(true);
        }

        const simuliveServiceUrl = rootGetters['g2wEnv/simuliveServiceUrl'];

        const {
          webinarKey, eventKey, registrantKey, participantId
        } = payload.joinInfo;

        let url = simuliveServiceUrl;
        url += `/events/${eventKey || webinarKey}/attendees/`;
        url += `${registrantKey}/participants/${participantId}`;

        const navigator = windowUtils.getGlobal('navigator');
        const supportsSendBeacon = !!navigator.sendBeacon;
        const sendBeaconResult = (supportsSendBeacon && navigator.sendBeacon(url));

        logger.log('leave', {supportsSendBeacon, sendBeaconResult});

        if (sendBeaconResult) {
          return left(true);
        }

        const fetchParams = {
          method: 'POST',
          headers: new Headers({
            'Content-Type': 'application/json'
          })
        };

        return fetcher.fetchJSON(url, fetchParams, {operation: 'finalizeAttendeeRecord'})
          .then(() => left(true))
          .catch(() => left(false));
      });
  },

  // global actions used by components

  sendQuestion: {
    root: true,
    async handler({dispatch}, payload) {
      await dispatch('g2wSession/questions/submit', payload, {root: true});
    }
  },

  fetchHandouts: {
    root: true,
    async handler({rootState, dispatch}) {
      const payload = {
        previewMode: rootState.previewMode
      };

      if (rootState.previewMode) {
        const {webinarKey} = rootState.g2wSession.joinInfo;
        const token = await dispatch('getToken', {webinarKey});
        payload.token = token.bearer;
      }

      await dispatch('g2wSession/handouts/getFiles', payload, {root: true});
    }
  },

  answerPoll: {
    root: true,
    async handler({dispatch}, payload) {
      dispatch('g2wSession/polls/addSubmission', payload, {root: true});
    }
  },

  getSurveyUrl: {
    root: true,
    handler({rootState}) {
      const extra = (rootState.previewMode) ? '/preview' : '';
      return rootState.g2wSession.joinInfo.surveyUrl
        && `${rootState.g2wSession.joinInfo.surveyUrl}${extra}`;
    }
  },

  async getSettings({commit, rootGetters}, payload) {
    const operation = 'getSettings';
    const webinarKey = payload.webinarKey;
    logger.log(operation, { webinarKey });

    const settingsServiceUrl = rootGetters['g2wEnv/settingsServiceUrl'];
    const url = `${settingsServiceUrl}/products/G2W/identifiers/WEBINAR-${webinarKey}`

    const settings = await fetcher.fetchJSON(url, {}, {operation});
    commit('setSettings', {settings})
    return settings;
  }
};

const state = () => ({
  settings: {
    showVideoControls: false
  }
});

export default {
  namespaced: true,
  actions,
  state,
  mutations
};
